/* eslint-disable import/no-anonymous-default-export */
import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { RefreshTokenResponse } from "@microtica/ms-usermanagement-sdk";
import { getUserManagementService, getIdToken } from ".";
import { setRecoil } from "recoil-nexus";
import { paymentPlanRequiredModalState } from "../recoil/payment-plan-required";
import { mfaRequiredModalState } from "../recoil/mfa-required";
import { clearSession } from "../utils/mfa-step-up";

type Subscriber = {
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
    config?: any;
};

let refreshPromise: Promise<void> | undefined;

// This is the list of waiting requests that will retry after the JWT refresh complete
let subscribers = [] as Subscriber[];

export const registerRequestInterceptor = (instance: AxiosInstance) => {
    instance.interceptors.response.use(
        // If the request succeeds, we don't have to do anything and just return the response
        response => {
            return response;
        },
        error => {
            const errorResponse = error.response;

            if (isPaymentPlanRequiredError(errorResponse)) {
                handlePaymentPlanRequiredError();
                // this supresses the notification.error popup because it returns an object with different format than expected, so it breaks 
                // alternative to this is to handle the 402 error from backend on every request that has a isPaymentLimited flag
                return Promise.reject({
                    code: 402,
                    message: undefined
                });
            }
            if (isMfaRequiredError(errorResponse)) {
                handleMfaRequiredError(errorResponse);
                // Same suppression as the 402 above: the global modal explains the
                // error, so the generic notification must not also fire. The code is
                // kept so sign-in and the policy page can still branch on it.
                return Promise.reject({
                    code: "MFA_REQUIRED",
                    status: 403,
                    message: undefined
                });
            }
            if (isRefreshTokenExpiredError(errorResponse)) {
                logout();
            }
            if (isTokenExpiredError(errorResponse)) {
                return resetTokenAndReattemptRequest(error);
            }
            if (isTokenInvalidError(errorResponse)) {
                logout();
            }

            // If the error is due to other reasons, we just throw it back to axios
            return Promise.reject({
                ...error,
                status: errorResponse?.status
            })
        }
    );
}

function isPaymentPlanRequiredError(
    errorResponse?: AxiosResponse<{
        code: number;
        message: string;
        errorCode?: string;
        type: string
    }>
): boolean {
    return errorResponse?.data.code === 402 &&
        // we want the Payment required modal to be displayed only on other errors
        errorResponse?.data.errorCode !== "BUILD_MINUTES_EXCEEDED" &&
        errorResponse?.data.errorCode !== "MANUAL_SCALING_UNAVAILABLE" &&
        errorResponse?.data.errorCode !== "AUTO_SCALING_UNAVAILABLE" &&
        errorResponse?.data.errorCode !== "DEPLOYMENTS_USEDD"
}

function isMfaRequiredError(errorResponse?: AxiosResponse<{ code?: string | number }>): boolean {
    return errorResponse?.data?.code === "MFA_REQUIRED";
}

function isTokenExpiredError(
    errorResponse: AxiosResponse<{
        code: number;
        message: string;
        type: string
    }>
): boolean {
    return errorResponse?.data.code === 401 && errorResponse.data.type === "TokenExpiredError";
}

function isTokenInvalidError(
    errorResponse: AxiosResponse<{
        code: number;
        message: string;
        type: string
    }>
): boolean {
    return errorResponse?.data.code === 401 && errorResponse.data.type === "JsonWebTokenError";
}

function isRefreshTokenExpiredError(errorResponse?: AxiosResponse<any>): boolean {
    const url = errorResponse?.config?.url || "";
    if (!/\/users\/refresh-token(?:[/?]|$)/.test(url)) {
        return false;
    }
    const code = errorResponse?.data?.code;
    if (errorResponse?.status === 401 && (code === "NOT_AUTHORIZED" || code === "AUTHENTICATION_FAILED")) {
        return true;
    }
    try {
        return (errorResponse?.status === 401 || errorResponse?.data?.code === 401) &&
            JSON.parse(errorResponse?.data?.message || "{}").code === "NotAuthorizedException";
    } catch (error) {
        return false;
    }
}

function getRefreshToken(): string | null {
    return localStorage.getItem("refreshToken");
}

function refreshLocalStorage(response: RefreshTokenResponse & { refreshToken?: string }): void {
    localStorage.setItem("idToken", response.idToken!);
    if (response.accessToken) {
        localStorage.setItem("accessToken", response.accessToken);
    }
    if (response.refreshToken) {
        localStorage.setItem("refreshToken", response.refreshToken);
    }
    localStorage.setItem("expirationDate", new Date(
        new Date().getTime() + response.expiresIn! * 1000
    ).toString());
}

async function resetTokenAndReattemptRequest(error: AxiosError) {
    const { response: errorResponse } = error;
    const refreshToken = getRefreshToken();
    const idToken = getIdToken();
    if (!refreshToken || !idToken) {
        return Promise.reject(error);
    }

    const retryOriginalRequest = new Promise((resolve, reject) => {
        addSubscriber({ resolve, reject, config: errorResponse?.config });
    });

    if (!refreshPromise) {
        refreshPromise = refreshTokens(refreshToken, idToken).finally(() => {
            refreshPromise = undefined;
        });
        // Waiting request promises carry the refresh failure to callers.
        // Mark this shared coordinator as handled to avoid an unhandled rejection.
        void refreshPromise.catch(() => undefined);
    }

    return retryOriginalRequest;
}

/**
 * Forces a token refresh outside the response interceptor and reuses the same
 * single-flight promise. Callers use it to backfill a missing access token.
 */
export async function refreshSession(): Promise<void> {
    const refreshToken = getRefreshToken();
    const idToken = getIdToken();
    if (!refreshToken || !idToken) {
        throw new Error("There is no session to refresh.");
    }

    if (!refreshPromise) {
        refreshPromise = refreshTokens(refreshToken, idToken).finally(() => {
            refreshPromise = undefined;
        });
        void refreshPromise.catch(() => undefined);
    }

    return refreshPromise;
}

async function refreshTokens(refreshToken: string, idToken: string) {
    try {
        const response = await getUserManagementService().refreshToken({ refreshToken, accessToken: "Bearer " + idToken });
        if (!response.data?.idToken) {
            throw new Error("The token refresh response did not include an ID token.");
        }
        refreshLocalStorage(response.data);
        onIdTokenFetched(response.data.idToken, response.data.accessToken);
    } catch (error) {
        onTokenRefreshFailed(error);
        throw error;
    }
}

function onIdTokenFetched(idToken: string, accessToken?: string) {
    // When the refresh is successful, we start retrying the requests one by one and empty the queue
    subscribers.forEach(subscriber => {
        if (!subscriber.config) {
            subscriber.reject(new Error("The request could not be retried after token refresh."));
            return;
        }
        subscriber.config.headers = {
            ...(subscriber.config.headers || {}),
            Authorization: "Bearer " + idToken,
            ...(accessToken && hasHeader(subscriber.config.headers, "X-Cognito-Access-Token") ? { "X-Cognito-Access-Token": accessToken } : {})
        };
        subscriber.resolve(axios(subscriber.config));
    });
    subscribers = [];
}

function hasHeader(headers: any, name: string): boolean {
    if (typeof headers?.has === "function") {
        return headers.has(name);
    }
    return Object.keys(headers || {}).some(key => key.toLowerCase() === name.toLowerCase());
}

function onTokenRefreshFailed(error: unknown) {
    subscribers.forEach(subscriber => subscriber.reject(error));
    subscribers = [];
}

function addSubscriber(subscriber: Subscriber) {
    subscribers.push(subscriber);
}


function logout() {
    clearSession();
    (window as Window).location = "/auth/login";
}

function handlePaymentPlanRequiredError() {
    // Display PaymentPlanRequiredModal
    setRecoil(paymentPlanRequiredModalState, { visible: true });
}

export function isMfaRequired(error: any): boolean {
    return error?.code === "MFA_REQUIRED" || error?.response?.data?.code === "MFA_REQUIRED";
}

function handleMfaRequiredError(errorResponse?: AxiosResponse<any>) {
    // Sign-in handles the policy itself by sending the user to Account security.
    if (window.location.pathname.startsWith("/auth/")) {
        return;
    }
    const url: string = errorResponse?.config?.url || "";
    const projectId = /\/projects\/([^/?#]+)/.exec(url)?.[1];

    // Display MfaRequiredModal
    setRecoil(mfaRequiredModalState, { visible: true, projectId });
}
